import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Skeleton,
  Divider,
  Button,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { DetailPanel } from 'components/shared/DetailPanel';
import { DetailPanelHeader } from 'components/shared/DetailPanelHeader';
import ContactInfoSection from './sections/ContactInfoSection';
import LinkedCustomersSection from './sections/LinkedCustomersSection';
import ContactFormUnified from 'containers/pages/contacts/components/ContactFormUnified/ContactFormUnified';

interface ContactDetailPanelProps {
  open: boolean;
  onClose: () => void;
  contactData: Record<string, unknown> | null;
  loading: boolean;
  error: string | null;
  onRefetch: () => void;
  companies?: unknown[];
  contactTypes?: unknown[];
  languageTypes?: unknown[];
  onFetchContacts?: () => Promise<void> | void;
}

function getFullName(contact: Record<string, unknown>): string {
  // v3 flat
  if (contact.FullName && typeof contact.FullName === 'string') {
    return contact.FullName;
  }
  // v3.2 nested
  const first = (contact.FirstName || contact.firstName || '') as string;
  const last = (contact.LastName || contact.lastName || '') as string;
  return `${first} ${last}`.trim() || 'Unknown Contact';
}

function getWorkEmail(contact: Record<string, unknown>): string {
  return (
    (contact.emailWork as string) ||
    (contact.WorkEmail as string) ||
    (contact.EmailWork as string) ||
    ''
  );
}

function getContactId(contact: Record<string, unknown>): string | number {
  return (contact.Id ?? contact.id ?? '') as string | number;
}

function LoadingSkeleton() {
  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Contact Info section skeleton */}
      <Skeleton variant="text" width={140} height={20} />
      {[1, 2, 3, 4].map((i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Skeleton variant="circular" width={32} height={32} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width={80} height={16} />
            <Skeleton variant="text" width={160} height={20} />
          </Box>
        </Box>
      ))}
      <Skeleton variant="rectangular" height={1} />
      {/* Linked Customers skeleton */}
      <Skeleton variant="text" width={130} height={20} />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Skeleton variant="rounded" width={100} height={28} />
        <Skeleton variant="rounded" width={120} height={28} />
      </Box>
    </Box>
  );
}

export default function ContactDetailPanel({
  open,
  onClose,
  contactData,
  loading,
  error,
  onRefetch,
  companies,
  contactTypes,
  languageTypes,
  onFetchContacts,
}: ContactDetailPanelProps) {
  const [editOpen, setEditOpen] = useState(false);

  const handleEditToggle = useCallback(() => {
    setEditOpen((prev) => !prev);
  }, []);

  const handleEditSave = useCallback(async () => {
    setEditOpen(false);
    onRefetch();
    if (onFetchContacts) {
      await onFetchContacts();
    }
  }, [onRefetch, onFetchContacts]);

  const handleClose = useCallback(() => {
    setEditOpen(false);
    onClose();
  }, [onClose]);

  const title = contactData ? getFullName(contactData) : 'Loading...';
  const subtitle = contactData ? getWorkEmail(contactData) : undefined;
  const contactId = contactData ? getContactId(contactData) : null;

  const headerActions = contactData ? (
    <Tooltip title="Edit Contact">
      <IconButton
        size="small"
        onClick={handleEditToggle}
        aria-label="Edit contact"
        sx={{ color: 'inherit' }}
      >
        <EditIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : undefined;

  return (
    <DetailPanel
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      header={
        <DetailPanelHeader
          title={title}
          subtitle={subtitle || undefined}
          backgroundColor="#546e7a"
          onClose={handleClose}
          actions={headerActions}
        />
      }
    >
      {loading ? (
        <LoadingSkeleton />
      ) : error ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error" variant="body1">
            {error}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={onRefetch}
            sx={{ mt: 2 }}
          >
            Retry
          </Button>
        </Box>
      ) : contactData ? (
        <>
          <ContactInfoSection contact={contactData} />

          <Divider />

          <LinkedCustomersSection contact={contactData} />

          <Divider />

          {/* Edit Section */}
          <Box sx={{ px: 2, py: 2 }}>
            {!editOpen && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEditToggle}
                fullWidth
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1,
                }}
              >
                Edit Contact
              </Button>
            )}
            <Collapse in={editOpen} timeout="auto" unmountOnExit>
              <Box sx={{ mt: editOpen ? 0 : 2 }}>
                <ContactFormUnified
                  mode="edit"
                  id={contactId}
                  companies={companies}
                  contactTypes={contactTypes}
                  languageTypes={languageTypes}
                  onFetchContacts={handleEditSave}
                />
              </Box>
            </Collapse>
          </Box>
        </>
      ) : null}
    </DetailPanel>
  );
}
